import React, { useState, useEffect } from 'react';
import { 
  Clock, Search, Filter, ArrowUpRight, 
  CheckCircle, XCircle, AlertCircle, 
  UserCheck, Calendar, ChevronDown, Download 
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
const API_BASE_URL = 'https://ior.org/risk-register/web/api';

const ApprovalHistoryTracker = () => {
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('6months');
  const [expandedItem, setExpandedItem] = useState(null);

    const { user } = useAuth();
    const token = localStorage.getItem('authToken');


  // Fetch approval history
  useEffect(() => {
    const fetchApprovalHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(
          `${API_BASE_URL}/approval-history.php?search=${searchTerm}&status=${filterStatus}&period=${filterPeriod}`
        );
        
        const data = await response.json();
        
        if (data.success) {
          setApprovalHistory(data.data);
        } else {
          throw new Error(data.error || 'Failed to fetch approval history');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search term changes
    const timeoutId = setTimeout(() => {
      fetchApprovalHistory();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterStatus, filterPeriod]);

  // Handle export
  const handleExport = async () => {
    try {
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/approval-history.php?format=csv&search=${searchTerm}&status=${filterStatus}&period=${filterPeriod}`,
        {
          headers: {
            'Accept': 'text/csv'
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'approval-history.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export history');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'approved': {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle,
        label: 'Fully Approved'
      },
      'pending-gb': {
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: AlertCircle,
        label: 'Pending GB Approval'
      },
      'pending-smt': {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: AlertCircle,
        label: 'Pending SMT Approval'
      },
      'pending': {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: AlertCircle,
        label: 'Pending Approval'
      },
      'rejected': {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircle,
        label: 'Rejected'
      }
    };

    console.log(status);

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const ApprovalTimeline = ({ timeline }) => (
    <div className="space-y-4">
      {timeline.map((item, index) => (
        <div key={index} className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
            <Clock className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <div className="flex items-baseline space-x-2">
              <span className="font-medium">{item.event}</span>
              <span className="text-sm text-gray-500">{item.date}</span>
            </div>
            <div className="text-sm text-gray-600">By {item.user}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const ApprovalLevel = ({ level }) => (
    <div className="bg-gray-50 p-4 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium">{level.level} Approval</span>
        <StatusBadge status={level.status} />
      </div>
      {level.approver && (
        <div className="space-y-2">
          <div className="flex items-center text-sm text-gray-600">
            <UserCheck className="w-4 h-4 mr-2" />
            {level.approver} - {level.role}
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="w-4 h-4 mr-2" />
            {level.date} ({level.meetingRef})
          </div>
          {level.comments && (
            <div className="text-sm text-gray-700 bg-white p-3 rounded">
              {level.comments}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderHeader = () => (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Approval History & Tracking</h1>
        <div className="text-gray-600">
          Track and monitor all risk approval activities
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      <div className="mb-6 grid grid-cols-3 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by risk title or ID..."
            className="pl-10 pr-4 py-2 w-full border rounded-md"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <select
            className="pl-10 pr-4 py-2 w-full border rounded-md appearance-none bg-white"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="pending-gb">Pending GB</option>
            <option value="pending-smt">Pending SMT</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="relative">
          <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <select
            className="pl-10 pr-4 py-2 w-full border rounded-md appearance-none bg-white"
            value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value)}
          >
            <option value="1month">Last Month</option>
            <option value="3months">Last 3 Months</option>
            <option value="6months">Last 6 Months</option>
            <option value="1year">Last Year</option>
          </select>
        </div>
      </div>

      <div className="mb-6 flex justify-end">
        <button 
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <Download className="w-4 h-4 mr-2" />
          Export History
        </button>
      </div>
    </>
  );

  const renderHistoryList = () => (
    loading ? (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    ) : approvalHistory.length === 0 ? (
      <div className="text-center py-8 text-gray-500">
        No approval history found
      </div>
    ) : (
      <div className="space-y-4">
        {approvalHistory.map((item) => (
          <div key={item.id} className="bg-white rounded-lg shadow">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <StatusBadge status={item.status} />
                  <div>
                    <div className="font-medium">{item.riskTitle}</div>
                    <div className="text-sm text-gray-500">
                      Risk ID: {item.riskId} • Category: {item.category}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ChevronDown 
                    className={`w-5 h-5 transform transition-transform ${
                      expandedItem === item.id ? 'rotate-180' : ''
                    }`}
                  />
                </button>
              </div>

              {expandedItem === item.id && (
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="font-medium">Approval Flow</h3>
                      {item.approvalFlow.map((level, index) => (
                        <ApprovalLevel key={index} level={level} />
                      ))}
                    </div>
                    <div>
                      <h3 className="font-medium mb-4">Timeline</h3>
                      <ApprovalTimeline timeline={item.timeline} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    )
  );

  return (
    <div className="max-w-7xl mx-auto">
      {renderHeader()}
      {renderHistoryList()}
    </div>
  );
};

export default ApprovalHistoryTracker;
